import { logger } from '@/shared/helpers/logger';

type GetAccessToken = (resource: string) => Promise<string | undefined>;
type OnTokenRefreshed = (token: string) => void;
type OnRefreshDead = () => void;

let _getAccessToken: GetAccessToken | null = null;
let _audience = '';
let _onTokenRefreshed: OnTokenRefreshed | null = null;
let _onRefreshDead: OnRefreshDead | null = null;
let _inflight: Promise<string | null> | null = null;
let _dead = false;
let _signOutClaimed = false;

export function registerTokenRefresh(
  getAccessToken: GetAccessToken,
  audience: string,
  onTokenRefreshed: OnTokenRefreshed,
  onRefreshDead?: OnRefreshDead,
): void {
  _getAccessToken = getAccessToken;
  _audience = audience;
  _onTokenRefreshed = onTokenRefreshed;
  _onRefreshDead = onRefreshDead ?? null;
  _dead = false;
  _signOutClaimed = false;
  _inflight = null;
}

export function isSessionDead(): boolean {
  return _dead;
}

export function claimSignOut(): boolean {
  _dead = true;
  if (_signOutClaimed) return false;

  _signOutClaimed = true;
  return true;
}

export function reportRefreshedTokenRejected(): void {
  if (_dead) return;

  _dead = true;
  logger.warn('the refreshed token was refused as well (session expired), signing out', {
    log_source: 'tokenRefresh',
  });
  _onRefreshDead?.();
}

export async function refreshAccessToken(): Promise<string | null> {
  if (_dead || !_getAccessToken || !_audience) return null;

  _inflight ??= (async () => {
    try {
      const token = (await _getAccessToken(_audience)) ?? null;
      if (token) _onTokenRefreshed?.(token);
      return token;
    } catch (err) {
      _dead = true;
      logger.warn('token refresh rejected (session expired), signing out', {
        error: err instanceof Error ? err : new Error(String(err)),
        log_source: 'tokenRefresh',
      });
      _onRefreshDead?.();
      return null;
    } finally {
      _inflight = null;
    }
  })();

  return _inflight;
}
